import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import DOMPurify from 'dompurify';
import { Calendar, User, Clock, ArrowLeft, ArrowRight } from 'lucide-react';

const BlogDetailPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/blog/posts/${slug}`);
        
        if (!response.ok) {
          throw new Error('Blog post not found');
        }
        
        const data = await response.json();
        setPost(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen py-20">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse">
              <div className="h-8 bg-[var(--bg-secondary)] rounded mb-4"></div>
              <div className="h-4 bg-[var(--bg-secondary)] rounded w-3/4 mb-8"></div>
              <div className="h-64 bg-[var(--bg-secondary)] rounded mb-8"></div>
              <div className="space-y-4">
                <div className="h-4 bg-[var(--bg-secondary)] rounded"></div>
                <div className="h-4 bg-[var(--bg-secondary)] rounded"></div>
                <div className="h-4 bg-[var(--bg-secondary)] rounded w-5/6"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen py-20">
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl font-bold text-[var(--text-primary)] mb-4">Blog Post Not Found</h1>
            <p className="text-[var(--text-secondary)] mb-8">{error}</p>
            <Link to="/blog" className="btn-primary inline-flex items-center">
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back to Blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!post) return null;

  return (
    <>
      <Helmet>
        <title>{post.title} | Marqi Blog</title>
        <meta name="description" content={post.excerpt} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={post.featured_image} />
        <meta property="og:type" content="article" />
      </Helmet>
      
      <div className="min-h-screen py-20">
        <div className="container">
          {/* Back Button */}
          <div className="max-w-4xl mx-auto mb-8">
            <Link to="/blog" className="inline-flex items-center text-[var(--accent-primary)] hover:text-[var(--accent-hover)] transition-colors">
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back to Blog
            </Link>
          </div>

          {/* Article Header */}
          <article className="max-w-4xl mx-auto">
            {post.featured && (
              <div className="inline-flex items-center px-3 py-1 bg-[var(--accent-bg)] rounded-full text-[var(--accent-primary)] text-sm font-medium mb-6">
                Featured Article
              </div>
            )}
            
            <div className="inline-flex items-center px-3 py-1 bg-[var(--bg-secondary)] rounded-full text-[var(--text-secondary)] text-sm font-medium mb-6">
              {post.category}
            </div>
            
            <h1 className="display-lg text-[var(--text-primary)] mb-6">{post.title}</h1>
            
            <p className="text-xl text-[var(--text-secondary)] mb-8 leading-relaxed">{post.excerpt}</p>
            
            {/* Article Meta */}
            <div className="flex flex-wrap gap-6 text-[var(--text-muted)] mb-8 pb-8 border-b border-[var(--border-subtle)]">
              <div className="flex items-center">
                <User className="w-4 h-4 mr-2" />
                {post.author}
              </div>
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                {formatDate(post.published_at || post.created_at)}
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                {post.read_time}
              </div>
            </div>

            {/* Featured Image */}
            {post.featured_image && (
              <div className="mb-12">
                <img
                  src={post.featured_image}
                  alt={post.title}
                  className="w-full h-96 object-cover rounded-lg"
                />
              </div>
            )}

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <div 
                className="text-[var(--text-primary)] leading-relaxed"
                style={{ 
                  fontSize: '1.1rem', 
                  lineHeight: '1.8',
                  fontFamily: 'inherit'
                }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content.replace(/\n/g, '<br />')) }}
              />
            </div>

            {/* Article Footer */}
            <div className="mt-12 pt-8 border-t border-[var(--border-subtle)]">
              <div className="flex justify-between items-center">
                <Link to="/blog" className="btn-secondary inline-flex items-center">
                  <ArrowLeft className="mr-2 w-4 h-4" />
                  More Articles
                </Link>
                
                <Link to="/contact" className="btn-primary inline-flex items-center">
                  Get Started
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </div>
            </div>
          </article>
        </div>
      </div>
    </>
  );
};

export default BlogDetailPage;